'use client';

import React, { useContext } from 'react';
import {
  DropdownToggle, DropdownMenu, Button,
  UncontrolledDropdown,
  DropdownItem,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { useDispatch } from 'react-redux';

import { useAppSelector } from 'src/hooks/redux-hooks';
import { AuthContext } from 'src/context/auth-context/auth-context';
import { subscribeToMRWatcher, unsubscribeFromMRWatcher } from 'src/redux/jobs/api';

import { updateWatcherList } from 'src/redux/jobs/slice';
import { createAbbreviation, handleErrorMessages } from 'src/helpers/utils';
import { isMakeReadyServiceCategory, updateUrlForOpenJobSidePanel } from 'src/helpers/job-slide-out';
import useFeatureFlags from 'src/hooks/use-feature-flags';
import type { AppDispatch } from 'src/types/redux';
import type { WatcherInfo } from 'src/types/jobs';

interface Watcher {
  property_manager_id?: number;
  full_name?: string;
  profile_picture?: string;
  [key: string]: unknown;
}

const WatcherDropdown: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [userDetails] = useContext(AuthContext);
  const job = useAppSelector((state) => state?.jobDetails?.data);
  const type = useAppSelector((state) => state?.jobDetails?.slideOut?.type);
  const isLoading = useAppSelector((state) => state?.jobDetails?.isLoading);
  const { makeReadySlideout } = useFeatureFlags();

  const watchers = job?.watchers as Watcher[] | undefined;
  const hasEnableWatcherSettings = (
    userDetails as Record<string, unknown>
  )?.has_enable_watcher_settings as boolean | undefined;

  const amIWatchingThisMR = watchers?.some(
    (watcher) => watcher?.property_manager_id
      === (userDetails as Record<string, unknown>)
        ?.property_manager_id,
  );

  const isMakeReadyJob = makeReadySlideout && isMakeReadyServiceCategory(job?.type, job?.service_category?.slug);

  const watchersCount = watchers?.length || 0;
  const tooltipContent = !watchersCount
    ? 'No watchers yet'
    : `${watchersCount} people are watchers`;

  const onUnsubscribeClickHandler = () => {
    dispatch(unsubscribeFromMRWatcher(job?.service_request_id)({})).unwrap()
      .then((rawResponse: unknown) => {
        const response = rawResponse as Record<string, unknown>;
        toast(response?.message as string, { type: 'success', theme: 'colored' });
        const newWatchers = watchers?.filter(
          (watcher) => watcher?.property_manager_id
            !== (userDetails as Record<string, unknown>)
              ?.property_manager_id,
        );
        dispatch(updateWatcherList(newWatchers as WatcherInfo[]));
      }).catch((error: Record<string, unknown>) => {
        toast(error?.message as string, { type: 'error', theme: 'colored' });
      });
  };

  const onWatcherClickHandler = () => {
    if (amIWatchingThisMR) {
      onUnsubscribeClickHandler();
    } else {
      dispatch(subscribeToMRWatcher(job?.service_request_id)({})).unwrap()
        .then((rawResponse: unknown) => {
          const response = rawResponse as Record<string, unknown>;
          toast(response?.message as string, { type: 'success', theme: 'colored' });
          dispatch(updateWatcherList(response?.watchers as WatcherInfo[]));
        }).catch((error: Record<string, unknown>) => {
          handleErrorMessages({
            errors: (error?.message as string) || error?.errors,
          }, (msg: string) => toast(msg, { type: 'error', theme: 'colored' }));
        });
    }
  };

  const onServiceRequestNoClick = () => {
    updateUrlForOpenJobSidePanel(
      job?.service_request_no as string,
      'serviceRequestDetails',
      '',
      job?.service_request_id,
    );
  };

  const renderDropDownMenuContent = () => {
    if (isLoading) {
      return (
        <div className="watcher-loader">
          <div className="skeleton-loader h-54 mb-8" />
          <div className="skeleton-loader w-30 mb-8" />
          <div className="skeleton-loader h-32 mb-8" />
          <div className="skeleton-loader h-32" />
          <div className="watcher-divider" />
          <div className="skeleton-loader" />
        </div>
      );
    }
    return (
      <React.Fragment>
        {type !== 'jobDetails' ? (
          <DropdownItem
            color="none"
            toggle={false}
            className={`watcher-button ${amIWatchingThisMR ? 'is-selected' : ''}`}
            onClick={onWatcherClickHandler}
          >
            <div className="watcher-button-wrapper">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.61342 8.47561C1.52262 8.33186 1.47723 8.25997 1.45182 8.14911C1.43273 8.06583 1.43273 7.9345 1.45182 7.85122C1.47723 7.74035 1.52262 7.66847 1.61341 7.52471C2.36369 6.33672 4.59693 3.3335 8.00027 3.3335C11.4036 3.3335 13.6369 6.33672 14.3871 7.52471C14.4779 7.66847 14.5233 7.74035 14.5487 7.85122C14.5678 7.9345 14.5678 8.06583 14.5487 8.14911C14.5233 8.25997 14.4779 8.33185 14.3871 8.47561C13.6369 9.6636 11.4036 12.6668 8.00027 12.6668C4.59693 12.6668 2.36369 9.6636 1.61342 8.47561Z" stroke="#101828" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M8.00027 10.0002C9.10484 10.0002 10.0003 9.10473 10.0003 8.00016C10.0003 6.89559 9.10484 6.00016 8.00027 6.00016C6.8957 6.00016 6.00027 6.89559 6.00027 8.00016C6.00027 9.10473 6.8957 10.0002 8.00027 10.0002Z" stroke="#101828" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div className="watcher-button-title">Watch</div>
              <svg className="tick-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.3334 4L6.00008 11.3333L2.66675 8" stroke="#1369E9" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p>Notify me on all key activities for this maintenance request</p>
          </DropdownItem>
        ) : '' }
        <DropdownItem header>
          Watchers
          {watchersCount ? (
            <span>
              {' '}
              (
              {watchersCount}
              )
            </span>
          ) : ''}
        </DropdownItem>
        {watchersCount ? (
          <DropdownItem tag="div" className="watcher-outer">
            {watchers
              ?.map((watcher) => (
                <DropdownItem tag="div" key={watcher?.property_manager_id} className="watcher-item">
                  <div className="watcher-wrapper">
                    <div className="watcher-image">
                      {watcher?.profile_picture
                        ? (
                          <img
                            src={watcher?.profile_picture}
                            alt={`${watcher?.full_name}'s profile`}
                          />
                        )
                        : <p>{createAbbreviation(watcher?.full_name)}</p>}

                    </div>
                    <div className="watcher-name">{watcher?.full_name}</div>
                  </div>
                  {watcher?.property_manager_id
                    === (userDetails as Record<string, unknown>)
                      ?.property_manager_id
                    && type !== 'jobDetails' ? (
                      <Button
                        color="none"
                        className="watcher-item--remove"
                        aria-label="close"
                        onClick={onUnsubscribeClickHandler}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9 3L3 9M3 3L9 9"
                            stroke="#101828"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Button>
                    ) : ''}
                </DropdownItem>
              ))}
          </DropdownItem>
        ) : ''}
        {!watchersCount ? (
          <DropdownItem
            tag="div"
            className="no-watchers"
          >
            No Watchers Yet
          </DropdownItem>
        )
          : ''}
        {type === 'jobDetails' ? (
          <React.Fragment>
            <DropdownItem divider />
            <DropdownItem text className="watcher-note">
              Note: to remove yourself as a watcher you need to do it from the
              {' '}
              <Button color="none" onClick={onServiceRequestNoClick}>maintenance request</Button>
            </DropdownItem>

          </React.Fragment>
        ) : ''}
      </React.Fragment>
    );
  };

  // Don't show watcher dropdown if user has not enabled watcher settings, for
  // Make-Ready jobs, or while loading — there's no separate loading placeholder
  // for the toggle button itself, so rendering it during loading and then
  // hiding it once we learn the job is Make-Ready would flash the button.
  if (!hasEnableWatcherSettings || isMakeReadyJob || isLoading) {
    return null;
  }

  return (
    <UncontrolledDropdown className="watcher-dropdown" color="none">
      <DropdownToggle
        className={`dropdown-toggle ${amIWatchingThisMR ? 'is-watching' : ''}`}
        data-tooltip-id="watcher"
        data-tooltip-content={tooltipContent}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1.61342 8.47561C1.52262 8.33186 1.47723 8.25997 1.45182 8.14911C1.43273 8.06583 1.43273 7.9345 1.45182 7.85122C1.47723 7.74035 1.52262 7.66847 1.61341 7.52471C2.36369 6.33672 4.59693 3.3335 8.00027 3.3335C11.4036 3.3335 13.6369 6.33672 14.3871 7.52471C14.4779 7.66847 14.5233 7.74035 14.5487 7.85122C14.5678 7.9345 14.5678 8.06583 14.5487 8.14911C14.5233 8.25997 14.4779 8.33185 14.3871 8.47561C13.6369 9.6636 11.4036 12.6668 8.00027 12.6668C4.59693 12.6668 2.36369 9.6636 1.61342 8.47561Z" stroke="#101828" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M8.00027 10.0002C9.10484 10.0002 10.0003 9.10473 10.0003 8.00016C10.0003 6.89559 9.10484 6.00016 8.00027 6.00016C6.8957 6.00016 6.00027 6.89559 6.00027 8.00016C6.00027 9.10473 6.8957 10.0002 8.00027 10.0002Z" stroke="#101828" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {watchersCount}
      </DropdownToggle>
      <Tooltip
        id="watcher"
        className="watcher-tooltip"
        positionStrategy="fixed"
        border="1px solid #D7E1F4"
        opacity={1}
      />
      <DropdownMenu>
        {renderDropDownMenuContent()}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

export default WatcherDropdown;